import { Button, ListItemText, Menu, MenuItem, SvgIcon, Typography } from '@mui/material';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import DotsIcon from 'assets/icons/dots.svg';
import DeleteDialog from 'components/common/DeleteDialog';
import { bool, object, string } from 'prop-types';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import channelShape from 'shapes/channel';
import { I18n } from 'utils/i18n';
import openAppConfig from 'utils/openAppConfig';
import useOpenClose from 'utils/useOpenClose';
import connector from './connector';

const DetailsButton = ({
  actions,
  company,
  channel,
  archived,
  authEmployee,
  app = null,
  isCompanyAdmin,
  isTeamAdmin,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [muteAnchorEl, setMuteAnchorEl] = useState(null);
  const [isOpenDialog, openDialog, closeDialog] = useOpenClose();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpenDialog) {
      setAnchorEl(null);
      setMuteAnchorEl(null);
    }
  }, [isOpenDialog]);

  const confirmDelete = useCallback(() => {
    actions.channels.destroy(channel._id);
    setTimeout(() => navigate(`/home/messages`), 10);
    closeDialog();
  }, [actions.channels, channel._id, closeDialog, navigate]);

  const handleClick = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setMuteAnchorEl(null);
  }, []);

  const handleMuteClose = useCallback(() => {
    setMuteAnchorEl(null);
  }, []);

  const handleEdit = useCallback(() => {
    actions.drawers.channel.open({ type: 'edit' });
    setAnchorEl(null);
    setMuteAnchorEl(null);
  }, [actions]);

  const handlePin = useCallback(() => {
    actions.channels.setPinned(company._id, authEmployee._id, channel._id, !channel.pinned);
    setAnchorEl(null);
    setMuteAnchorEl(null);
  }, [actions.channels, company._id, authEmployee._id, channel._id, channel.pinned]);

  const handleArchive = useCallback(() => {
    actions.channels.setArchived(company._id, authEmployee._id, channel._id, !archived);
    setAnchorEl(null);
    setMuteAnchorEl(null);
  }, [actions.channels, company._id, authEmployee._id, channel._id, archived]);

  const handleMute = useCallback(
    duration => () => {
      setMuteAnchorEl(null);
      actions.channels.setMuted(company._id, authEmployee._id, channel._id, duration);
      setAnchorEl(null);
    },
    [actions.channels, company._id, authEmployee._id, channel._id],
  );

  const openMedia = useCallback(
    type => () => {
      actions.drawers.media.open({ type });
      setAnchorEl(null);
      setMuteAnchorEl(null);
    },
    [actions],
  );

  const openMembers = useCallback(() => {
    actions.drawers.employeeList.open();
    setAnchorEl(null);
    setMuteAnchorEl(null);
  }, [actions]);

  const openApp = useCallback(() => {
    setAnchorEl(null);
    openAppConfig(actions, app)();
    setAnchorEl(null);
    setMuteAnchorEl(null);
  }, [actions, app]);

  const handleMuteEnter = useCallback(event => {
    setMuteAnchorEl(event.currentTarget);
  }, []);

  const handleMuteLeave = useCallback(() => {
    setMuteAnchorEl(null);
  }, []);

  const { isDirect, name } = channel;
  return (
    <div>
      <Button
        sx={theme => ({
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 1px 2px 0 rgba(2, 2, 3, 0.05)',
          border: `solid 1px ${theme.palette.divider}`,
          padding: '0 10px',
          color: 'black',
        })}
        aria-owns={anchorEl ? 'simple-menu' : null}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <SvgIcon
          sx={theme => ({
            fontSize: 18,
            marginRight: '12px',
            marginLeft: '7px',
            color: theme.palette.primary.main,
          })}
        >
          <DotsIcon />
        </SvgIcon>
        {I18n.t('DetailsDropdown.Details')}
      </Button>
      <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={openMedia('media')}>{I18n.t('DetailsDropdown.Media')}</MenuItem>
        <MenuItem onClick={openMedia('doc')}>{I18n.t('DetailsDropdown.Documents')}</MenuItem>
        <MenuItem onClick={openMedia('link')}>{I18n.t('DetailsDropdown.Links')}</MenuItem>
        <MenuItem onClick={openMedia('form')}>{I18n.t('DetailsDropdown.Forms')}</MenuItem>
        {!isDirect && (isCompanyAdmin || channel.isAdmin || !channel.isReadOnly)
          ? [
              <MenuItem key="members" onClick={openMembers}>
                {I18n.t('DetailsDropdown.Members')}
              </MenuItem>,
            ]
          : null}

        {(channel.isMuteable || channel.mutedUntil) &&
          (channel.mutedUntil ? (
            <MenuItem key="mute" onClick={handleMute(null)}>
              {I18n.t('DetailsDropdown.Unmute')}
            </MenuItem>
          ) : (
            <MenuItem key="mute" onMouseEnter={handleMuteEnter} onMouseLeave={handleMuteLeave}>
              <ListItemText sx={{ padding: 0, border: 0, minHeight: 0 }}>
                {I18n.t('DetailsDropdown.Mute')}
              </ListItemText>
              <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', mr: -2 }}>
                <ChevronRightIcon />
              </Typography>
              <Menu
                id="mute-menu"
                anchorEl={muteAnchorEl}
                open={Boolean(muteAnchorEl)}
                onClose={handleMuteClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                autoFocus={false}
                disableAutoFocus
                disableEnforceFocus
                style={{ pointerEvents: 'none' }}
              >
                <div style={{ pointerEvents: 'auto' }}>
                  <MenuItem onClick={handleMute('15m')}>
                    {I18n.t('DetailsDropdown.Mute 15 minutes')}
                  </MenuItem>
                  <MenuItem onClick={handleMute('1h')}>
                    {I18n.t('DetailsDropdown.Mute 1 hour')}
                  </MenuItem>
                  <MenuItem onClick={handleMute('8h')}>
                    {I18n.t('DetailsDropdown.Mute 8 hours')}
                  </MenuItem>
                  <MenuItem onClick={handleMute('1w')}>
                    {I18n.t('DetailsDropdown.Mute 1 week')}
                  </MenuItem>
                  <MenuItem onClick={handleMute('1000y')}>
                    {I18n.t('DetailsDropdown.Mute always')}
                  </MenuItem>
                </div>
              </Menu>
            </MenuItem>
          ))}

        <MenuItem key="edit" onClick={handleArchive}>
          {I18n.t(archived ? 'DetailsDropdown.Unarchive' : 'DetailsDropdown.Archive')}
        </MenuItem>
        {isCompanyAdmin && !channel.isDirect && (
          <MenuItem key="pin" onClick={handlePin}>
            {I18n.t(channel.pinned ? 'DetailsDropdown.Unpin' : 'DetailsDropdown.Pin')}
          </MenuItem>
        )}
        {(isCompanyAdmin || channel.isAdmin) && !isDirect
          ? [
              <MenuItem key="edit" onClick={handleEdit}>
                {I18n.t('DetailsDropdown.Edit Channel')}
              </MenuItem>,
              <MenuItem key="delete" onClick={openDialog} sx={{ color: 'error.dark' }}>
                {I18n.t('DetailsDropdown.Delete Channel')}
              </MenuItem>,
            ]
          : null}
        {isTeamAdmin && app && (
          <MenuItem key="app" onClick={openApp}>
            {I18n.t('DetailsDropdown.Open app')}
          </MenuItem>
        )}
      </Menu>
      <DeleteDialog
        typeBack={name}
        message={I18n.t('DetailsDropdown.ConfirmDelete', { name })}
        isOpen={isOpenDialog}
        onConfirm={confirmDelete}
        onClose={closeDialog}
      />
    </div>
  );
};

DetailsButton.propTypes = {
  app: string,
  actions: object.isRequired,
  isCompanyAdmin: bool.isRequired,
  isTeamAdmin: bool.isRequired,
  channel: channelShape.isRequired,
  archived: bool.isRequired,
  company: object.isRequired,
  authEmployee: object.isRequired,
};

const isEqual = (prev, next) => {
  return (
    prev.isCompanyAdmin === next.isCompanyAdmin &&
    prev.channel.isDirect !== next.channel.isDirect &&
    prev.channel.name === next.channel.name &&
    prev.channel.pinned === next.channel.pinned &&
    prev.channel._id === next.channel._id
  );
};

export default connector(memo(DetailsButton, isEqual));
